import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Sun, Moon, CreditCard, Settings, Crown, LogOut } from '@/components/icons';
import { useTheme } from '../../App';
import { useAuth } from '../../pages/Auth/AuthProvider';
import { useCommandMenu } from './CommandMenu';
import { signOut } from '../../lib/auth-client';
import { trackSignOut } from '../../lib/posthog';
import { resolveAssetUrl } from '../../lib/assets';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/beui';

/**
 * Slim dark top bar (Shopify-admin style): logo · centered search · account.
 * Stays dark in both themes — the signature two-tone chrome.
 */
export function TopBar() {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { open } = useCommandMenu();
  const initial = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
  const subscriptionTier =
    (user as { subscriptionTier?: string } | null)?.subscriptionTier || 'free';

  const handleSignOut = async () => {
    trackSignOut();
    await signOut();
  };

  return (
    <header className="z-30 flex h-8 flex-none items-center gap-3 rounded-lg bg-[var(--topbar)] px-1.5 text-[var(--topbar-foreground)] shadow-sm">
      <div className="flex w-auto flex-none items-center gap-2 md:w-[190px]">
        <img src="/StackSketch-IconLogo.svg" alt="" className="h-5 w-5 rounded" />
        <span className="hidden text-[13px] font-semibold tracking-tight sm:inline">StackSketch</span>
        <ChevronDown className="hidden h-3 w-3 text-[var(--topbar-muted)] sm:block" />
      </div>

      <button
        type="button"
        onClick={open}
        className="ss-press mx-auto flex h-[26px] w-full max-w-[560px] items-center gap-2 rounded-md border border-[var(--topbar-border)] bg-[var(--topbar-input)] px-2.5 text-[12.5px] text-[var(--topbar-muted)] transition-colors hover:border-white/25"
      >
        <Search className="h-3.5 w-3.5 flex-none" />
        <span className="truncate">Search diagrams, templates and folders</span>
        <kbd className="ml-auto rounded border border-[var(--topbar-border)] px-1.5 font-mono text-[10px]">⌘K</kbd>
      </button>

      <div className="flex w-auto flex-none items-center justify-end gap-0.5 md:w-[190px]">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="ss-press grid h-[26px] w-[26px] place-items-center rounded-md text-[var(--topbar-muted)] transition-colors hover:bg-white/10 hover:text-[var(--topbar-foreground)]"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger>
            <button
              type="button"
              aria-label="Account"
              className="ss-press ml-1 flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 outline-none transition-colors hover:bg-white/10 aria-expanded:bg-white/10"
            >
              {user?.image ? (
                <img
                  src={resolveAssetUrl(user.image)}
                  alt=""
                  className="h-[22px] w-[22px] rounded-full object-cover"
                />
              ) : (
                <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-gradient-to-br from-primary to-fuchsia-500 text-[10px] font-semibold text-white">
                  {initial}
                </span>
              )}
              <span className="hidden max-w-[92px] truncate text-[12.5px] sm:inline">{user?.name || 'Account'}</span>
              <ChevronDown className="hidden h-3 w-3 flex-none text-[var(--topbar-muted)] sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-foreground">{user?.name || 'Account'}</p>
                {subscriptionTier !== 'free' && (
                  <span
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground ${
                      subscriptionTier === 'lifetime'
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600'
                        : 'bg-primary'
                    }`}
                  >
                    <Crown className="h-2.5 w-2.5" />
                    {subscriptionTier.toUpperCase()}
                  </span>
                )}
              </div>
              {user?.email && (
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/billing')}>
              <CreditCard className="h-4 w-4" />
              Billing
            </DropdownMenuItem>
            {subscriptionTier === 'free' && (
              <DropdownMenuItem
                onSelect={() => navigate('/pricing')}
                className="text-primary"
              >
                <Crown className="h-4 w-4" />
                Upgrade to Pro
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => navigate('/settings')}>
              <Settings className="h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleSignOut}
              tone="destructive"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
